import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GlobalIncident } from '../../types/ScenarioManagement';
import { useTheme } from '../../contexts/ThemeContext';
import { JSONPersistenceService } from '../../services/JSONPersistenceService';
import { StandardButton } from '../ui';

interface GlobalIncidentSelectorProps {
  selectedIncidentIds: string[];
  onChange: (selectedIds: string[]) => void;
  maxSelections?: number;
}

const GlobalIncidentSelector: React.FC<GlobalIncidentSelectorProps> = ({
  selectedIncidentIds,
  onChange,
  maxSelections = 10
}) => {
  const { isLight } = useTheme();
  const [globalIncidents, setGlobalIncidents] = useState<GlobalIncident[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('all');

  useEffect(() => {
    loadGlobalIncidents();
  }, []);

  const loadGlobalIncidents = async () => {
    try {
      setLoading(true);
      const persistence = JSONPersistenceService.getInstance();
      const data = await persistence.loadData();
      
      if (data?.globalIncidents) {
        const incidents = data.globalIncidents
          .map(([id, incident]) => incident)
          .filter(inc => inc.isEnabled);
        setGlobalIncidents(incidents);
      } else {
        setGlobalIncidents([]);
      }
    } catch (err) {
      console.error('Failed to load global incidents:', err);
      setError('Failed to load incidents');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleIncident = (incidentId: string) => {
    const isSelected = selectedIncidentIds.includes(incidentId);
    
    if (isSelected) {
      // Remove from selection
      onChange(selectedIncidentIds.filter(id => id !== incidentId));
    } else {
      // Add to selection if under limit
      if (selectedIncidentIds.length < maxSelections) {
        onChange([...selectedIncidentIds, incidentId]);
      }
    }
  };

  const filteredIncidents = globalIncidents.filter(incident => {
    const matchesSearch = incident.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         incident.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || incident.category === selectedCategory;
    const matchesSeverity = selectedSeverity === 'all' || incident.severity === selectedSeverity;
    return matchesSearch && matchesCategory && matchesSeverity;
  });

  const categories = Array.from(new Set(globalIncidents.map(inc => inc.category)));
  const severities = Array.from(new Set(globalIncidents.map(inc => inc.severity)));

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'low': return 'text-green-500';
      case 'medium': return 'text-yellow-500';
      case 'high': return 'text-orange-500';
      case 'critical': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'low': return '🟢';
      case 'medium': return '🟡';
      case 'high': return '🟠';
      case 'critical': return '🔴';
      default: return '⚪';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'phishing': return '🎣';
      case 'malware': return '🦠';
      case 'ransomware': return '🔐';
      case 'supply-chain': return '🔗';
      case 'insider': return '👤';
      case 'ddos': return '⚡';
      case 'data-breach': return '💾';
      default: return '🚨';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-professional-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error}</p>
        <StandardButton
          onClick={loadGlobalIncidents}
          variant="primary"
        >
          Retry
        </StandardButton>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-semibold ${isLight ? 'text-slate-800' : 'text-white'}`}>
            Select Incidents
          </h3>
          <p className={`text-sm ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>
            {selectedIncidentIds.length} of {maxSelections} selected
          </p>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search incidents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full px-4 py-2 rounded-lg border ${
              isLight 
                ? 'border-slate-300 bg-white text-slate-900 placeholder-slate-500' 
                : 'border-white/20 bg-white/5 text-white placeholder-slate-400'
            } focus:ring-2 focus:ring-professional-blue-500 focus:border-transparent`}
          />
        </div>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className={`px-4 py-2 rounded-lg border ${
            isLight 
              ? 'border-slate-300 bg-white text-slate-900' 
              : 'border-white/20 bg-white/5 text-white'
          } focus:ring-2 focus:ring-professional-blue-500 focus:border-transparent`}
        >
          <option value="all">All Categories</option>
          {categories.map(category => (
            <option key={category} value={category}>
              {getCategoryIcon(category)} {category.charAt(0).toUpperCase() + category.slice(1)}
            </option>
          ))}
        </select>
        <select
          value={selectedSeverity}
          onChange={(e) => setSelectedSeverity(e.target.value)}
          className={`px-4 py-2 rounded-lg border ${
            isLight 
              ? 'border-slate-300 bg-white text-slate-900' 
              : 'border-white/20 bg-white/5 text-white'
          } focus:ring-2 focus:ring-professional-blue-500 focus:border-transparent`}
        >
          <option value="all">All Severities</option>
          {severities.map(severity => (
            <option key={severity} value={severity}>
              {getSeverityIcon(severity)} {severity.charAt(0).toUpperCase() + severity.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {/* Incident Grid */}
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        layout
      >
        <AnimatePresence>
          {filteredIncidents.map((incident) => {
            const isSelected = selectedIncidentIds.includes(incident.id);
            const canSelect = isSelected || selectedIncidentIds.length < maxSelections;

            return (
              <motion.div
                key={incident.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className={`p-4 rounded-lg border transition-all duration-200 cursor-pointer ${
                  isSelected
                    ? isLight
                      ? 'bg-professional-blue-50 border-professional-blue-300 shadow-md'
                      : 'bg-professional-blue-500/20 border-professional-blue-400 shadow-md'
                    : canSelect
                    ? isLight
                      ? 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md'
                      : 'bg-white/5 border-white/10 hover:border-white/20 hover:bg-white/10'
                    : isLight
                    ? 'bg-slate-100 border-slate-200 opacity-50 cursor-not-allowed'
                    : 'bg-white/5 border-white/10 opacity-50 cursor-not-allowed'
                }`}
                onClick={() => canSelect && handleToggleIncident(incident.id)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{incident.icon}</span>
                    <span className="text-lg">{getCategoryIcon(incident.category)}</span>
                    <span className="text-lg">{getSeverityIcon(incident.severity)}</span>
                  </div>
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    isSelected
                      ? 'bg-professional-blue-600 border-professional-blue-600'
                      : isLight
                      ? 'border-slate-300'
                      : 'border-white/30'
                  }`}>
                    {isSelected && (
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </div>

                <h4 className={`font-semibold mb-2 ${
                  isLight ? 'text-slate-800' : 'text-white'
                }`}>
                  {incident.title}
                </h4>

                <p className={`text-sm mb-3 line-clamp-2 ${
                  isLight ? 'text-slate-600' : 'text-slate-300'
                }`}>
                  {incident.description}
                </p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`text-sm px-2 py-1 rounded ${
                      isLight
                        ? 'bg-slate-100 text-slate-600'
                        : 'bg-white/10 text-slate-300'
                    }`}>
                      {getCategoryIcon(incident.category)} {incident.category}
                    </span>
                    <span className={`text-sm font-medium ${getSeverityColor(incident.severity)}`}>
                      {incident.severity.toUpperCase()}
                    </span>
                  </div>
                  <span className={`text-sm font-medium ${
                    isLight ? 'text-slate-700' : 'text-slate-200'
                  }`}>
                    {Math.floor(incident.timeLimit / 60)}m {incident.timeLimit % 60}s
                  </span>
                </div>

                <div className="mt-2 text-xs text-center">
                  <span className={`px-2 py-1 rounded ${
                    isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/10 text-slate-400'
                  }`}>
                    {incident.responseOptions.length} response options
                  </span>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </motion.div>

      {filteredIncidents.length === 0 && (
        <div className="text-center py-12">
          <p className={`text-lg ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>
            No incidents found matching your criteria
          </p>
        </div>
      )}
    </div>
  );
};

export default GlobalIncidentSelector;